import {PersistedPlateMap} from 'samples/plate-maps/plate-record/PlateRecordConstants';
import {WellPosition} from 'samples/plate-maps/PlateMapConstants';

export enum PlateMapErrorLevel {
  ERROR = 'ERROR',
  WARNING = 'WARNING',
  INFO = 'INFO',
}

export const PLATE_MAP_ERROR_LEVEL_PRECEDENCE = {
  [PlateMapErrorLevel.ERROR]: 3,
  [PlateMapErrorLevel.WARNING]: 2,
  [PlateMapErrorLevel.INFO]: 1,
};

/** The more severe of two error levels; a null level never outranks a present one. */
export function moreSevereErrorLevel(
  a: PlateMapErrorLevel | null,
  b: PlateMapErrorLevel | null
): PlateMapErrorLevel | null {
  if (a == null || b == null) {
    return a ?? b;
  }
  return PLATE_MAP_ERROR_LEVEL_PRECEDENCE[a] >= PLATE_MAP_ERROR_LEVEL_PRECEDENCE[b] ? a : b;
}

export enum PlateMapErrorType {
  LAYER = 'LAYER',
  WELL = 'WELL',
  FIELD_VALUE = 'FIELD_VALUE',
  REAGENT = 'REAGENT',
  REAGENT_VALUE = 'REAGENT_VALUE',
}

export enum PlateMapReagentType {
  SOURCE = 'SOURCE',
  CONTENT = 'CONTENT',
}

export enum PlateMapReagentValueType {
  MATERIAL_INPUT = 'MATERIAL_INPUT',
  SOURCE_CONCENTRATION = 'SOURCE_CONCENTRATION',
  TRANSFER_QUANTITY = 'TRANSFER_QUANTITY',
  FINAL_CONCENTRATION = 'FINAL_CONCENTRATION',
  FINAL_QUANTITY = 'FINAL_QUANTITY',
}

export enum MeasurementComponent {
  VALUE = 'VALUE',
  UNIT = 'UNIT',
}

export interface BasePlateMapError<TError extends PlateMapErrorType> {
  type: TError;
  level: PlateMapErrorLevel;
  message: string;
}

export interface LayerPlateMapError extends BasePlateMapError<PlateMapErrorType.LAYER> {
  layerId: string;
}

export interface WellPlateMapError extends BasePlateMapError<PlateMapErrorType.WELL> {
  layerId: string;
  coordinates: WellPosition;
}

export interface FieldValuePlateMapError extends BasePlateMapError<PlateMapErrorType.FIELD_VALUE> {
  layerId: string;
  coordinates: WellPosition;
  value: string;
  url?: string;
}

export interface ReagentPlateMapError extends BasePlateMapError<PlateMapErrorType.REAGENT> {
  reagentId: string;
}

export interface BaseReagentValuePlateMapError<
  TReagentValue extends PlateMapReagentValueType,
> extends BasePlateMapError<PlateMapErrorType.REAGENT_VALUE> {
  reagentValueType: TReagentValue;
  coordinates: WellPosition;
}

export interface MaterialInputPlateMapError extends BaseReagentValuePlateMapError<PlateMapReagentValueType.MATERIAL_INPUT> {
  reagentType: PlateMapReagentType;
  containableId: string;
  containerId?: string;
}

export interface SourceConcentrationPlateMapError extends BaseReagentValuePlateMapError<PlateMapReagentValueType.SOURCE_CONCENTRATION> {
  reagentType: PlateMapReagentType;
  measurementComponent: MeasurementComponent;
  containableId: string;
}

export interface TransferQuantityPlateMapError extends BaseReagentValuePlateMapError<PlateMapReagentValueType.TRANSFER_QUANTITY> {
  reagentType: PlateMapReagentType;
  measurementComponent: MeasurementComponent;
  reagentId: string;
}

export interface FinalConcentrationPlateMapError extends BaseReagentValuePlateMapError<PlateMapReagentValueType.FINAL_CONCENTRATION> {
  reagentType: PlateMapReagentType;
  measurementComponent: MeasurementComponent;
  containableId: string;
  containerId?: string;
}

export interface FinalQuantityPlateMapError extends BaseReagentValuePlateMapError<PlateMapReagentValueType.FINAL_QUANTITY> {
  measurementComponent: MeasurementComponent;
}

export type ReagentValuePlateMapError =
  | MaterialInputPlateMapError
  | SourceConcentrationPlateMapError
  | TransferQuantityPlateMapError
  | FinalConcentrationPlateMapError
  | FinalQuantityPlateMapError;

/**
 * Plate map error types that are not hydrated into the plate map itself, including errors for
 * schema field annotations and reagent errors but not ReagentValuePlateMapErrors.
 */
export type PlateMapAnnotationError =
  | LayerPlateMapError
  | WellPlateMapError
  | FieldValuePlateMapError
  | ReagentPlateMapError;

// Keep in sync with the dataclass of the same name in
// benchling/inventory/plate_map/core/plate_map_validation_types.py
export interface PlateMapValidationResponse {
  layerValidation: Array<LayerPlateMapError>;
  wellValidation: Array<WellPlateMapError>;
  fieldValueValidation: Array<FieldValuePlateMapError>;
  reagentValidation: Array<ReagentPlateMapError>;
  reagentValueValidation: Array<ReagentValuePlateMapError>;
}

export interface PlateDesignValidationResponse {
  sanitizedPlateMap: PersistedPlateMap;
  plateMapValidation: PlateMapValidationResponse;
}

/**
 * Errors for a plate map from continuous validation except those hydrated in the plate map.
 */
export type PlateMapValidation = Omit<PlateMapValidationResponse, 'reagentValueValidation'>;

export interface PlateMapAnnotationErrorWithUUID {
  uuid: string;
  error: PlateMapAnnotationError;
}
export type ValidationErrorsByLevel = {
  [key in PlateMapErrorLevel]?: Array<PlateMapAnnotationErrorWithUUID>;
};

export interface ValidationErrorsByLayerAndLevel {
  [id: string]: ValidationErrorsByLevel;
}
